import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dumbbell, ChevronDown, ChevronUp, Target, Clock, Timer, AlertCircle,
  TrendingUp, Layers, Loader2, ArrowUpRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  lower: "Lower Body",
  upper_push: "Upper Push",
  upper_pull: "Upper Pull",
  posterior: "Posterior Chain",
};

const CATEGORY_COLORS = {
  lower: "#FF4500",       // orange
  upper_push: "#CCFF00",  // accent green
  upper_pull: "#3B82F6",  // blue
  posterior: "#A855F7",   // purple
};

const TIERS = [
  { id: "beginner",    label: "Beginner",     subtitle: "Home / Dumbbell",  color: "#10B981" },
  { id: "intermediate",label: "Intermediate", subtitle: "Barbell · Foundation", color: "#F59E0B" },
  { id: "advanced",    label: "Advanced",     subtitle: "Heavy · Intent",   color: "#EF4444" },
];

const WeightRoomPage = () => {
  const [lifts, setLifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTier, setActiveTier] = useState("intermediate");
  const [expanded, setExpanded] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/lifts`);
        setLifts(res.data.lifts || []);
      } catch { /* swallow */ } finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(lifts.map((l) => l.category));
    return ["all", ...Array.from(set)];
  }, [lifts]);

  const filtered = activeCategory === "all"
    ? lifts
    : lifts.filter((l) => l.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative" data-testid="weight-room-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto space-y-6"
      >
        {/* Hero */}
        <div className="relative">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">Strength Lab</p>
          <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">
            Weight Room
          </h1>
          <p className="text-sm text-white/50 mt-3 max-w-2xl font-mono">
            Eight foundational lifts. Three skill tiers each.
            Pick your level — the program tells you exactly how to load, brace, and progress.
          </p>
        </div>

        {/* Tier selector — the user's level */}
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-4" data-testid="tier-selector">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">
            Pick your skill tier
          </p>
          <div className="grid grid-cols-3 gap-2">
            {TIERS.map((t) => {
              const active = activeTier === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTier(t.id)}
                  data-testid={`tier-btn-${t.id}`}
                  className="text-left p-3 transition-all border"
                  style={{
                    background: active ? `${t.color}15` : "#050505",
                    borderColor: active ? t.color : "rgba(255,255,255,0.06)",
                  }}
                >
                  <p
                    className="text-sm font-heading uppercase tracking-wider"
                    style={{ color: active ? t.color : "rgba(255,255,255,0.8)" }}
                  >
                    {t.label}
                  </p>
                  <p className="text-[10px] font-mono text-white/40 mt-0.5">{t.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2" data-testid="category-filter">
          {categories.map((c) => {
            const active = activeCategory === c;
            const label = c === "all" ? "All Lifts" : CATEGORY_LABELS[c] || c;
            const color = c === "all" ? "#FFFFFF" : CATEGORY_COLORS[c] || "#FFFFFF";
            return (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                data-testid={`cat-${c}`}
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border transition-all"
                style={{
                  background: active ? `${color}15` : "transparent",
                  borderColor: active ? color : "rgba(255,255,255,0.08)",
                  color: active ? color : "rgba(255,255,255,0.5)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/40 font-mono text-sm py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> LOADING LIFTS...
          </div>
        ) : (
          <div className="space-y-3" data-testid="lifts-list">
            {filtered.map((lift) => (
              <LiftCard
                key={lift.id}
                lift={lift}
                tier={activeTier}
                expanded={expanded === lift.id}
                onToggle={() => setExpanded(expanded === lift.id ? null : lift.id)}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ============================================================
// Lift Card — collapsible with tier-focused detail
// ============================================================
const LiftCard = ({ lift, tier, expanded, onToggle }) => {
  const tierData = lift.tiers[tier];
  const catColor = CATEGORY_COLORS[lift.category] || "#FFFFFF";
  const tierMeta = TIERS.find((t) => t.id === tier);

  return (
    <div
      className="bg-[#0A0A0A] border overflow-hidden transition-all"
      style={{ borderColor: expanded ? catColor : "rgba(255,255,255,0.06)" }}
      data-testid={`lift-${lift.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-start gap-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid={`lift-toggle-${lift.id}`}
      >
        <div
          className="w-12 h-12 flex items-center justify-center flex-shrink-0"
          style={{ background: `${catColor}15`, color: catColor }}
        >
          <Dumbbell className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-xl font-heading text-white uppercase">{lift.name}</h3>
            <span
              className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5"
              style={{ background: `${catColor}22`, color: catColor }}
            >
              {CATEGORY_LABELS[lift.category] || lift.category}
            </span>
          </div>
          <p className="text-xs text-white/60 mb-2">{tierData.name}</p>
          <div className="flex flex-wrap gap-3 text-[11px] font-mono text-white/40">
            <span style={{ color: tierMeta.color }}>● {tierMeta.label}</span>
            <span>· {tierData.sets_reps}</span>
            <span>· {tierData.rest_seconds}s rest</span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-white/30 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-5 border-t border-white/[0.06]">
              {/* Overview */}
              <div className="pt-5">
                <p className="text-sm text-white/80 leading-relaxed">{lift.description}</p>
              </div>

              <div
                className="p-3 border-l-2"
                style={{
                  borderColor: catColor,
                  background: `${catColor}08`,
                }}
              >
                <p className="text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: catColor }}>
                  🏀 Basketball Benefit
                </p>
                <p className="text-xs text-white/70 italic leading-relaxed">{lift.basketball_benefit}</p>
              </div>

              {/* Muscles */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-1">Primary</p>
                  <p className="text-xs text-white/70">{lift.primary_muscles.join(", ")}</p>
                </div>
                <div>
                  <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-1">Secondary</p>
                  <p className="text-xs text-white/70">{lift.secondary_muscles.join(", ")}</p>
                </div>
              </div>

              {/* Tier program block */}
              <div
                className="p-4 border"
                style={{
                  borderColor: tierMeta.color,
                  background: `${tierMeta.color}08`,
                }}
                data-testid={`lift-tier-${lift.id}-${tier}`}
              >
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" style={{ color: tierMeta.color }} />
                    <h4
                      className="text-sm font-heading uppercase tracking-wider"
                      style={{ color: tierMeta.color }}
                    >
                      {tierMeta.label} · {tierData.name}
                    </h4>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <Stat icon={Target} label="Sets × Reps" value={tierData.sets_reps} />
                  <Stat icon={Clock} label="Rest" value={`${tierData.rest_seconds}s`} />
                  <Stat icon={Timer} label="Tempo" value={tierData.tempo} small />
                  <Stat icon={Dumbbell} label="Equipment" value={tierData.equipment.join(" + ")} small />
                </div>

                <div className="mb-4">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-white/50 mb-1">
                    Load Guidance
                  </p>
                  <p className="text-xs text-white/80 leading-relaxed">{tierData.load_guidance}</p>
                </div>

                {/* Coaching cues */}
                <div className="mb-4">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-white/50 mb-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Coaching Cues
                  </p>
                  <ul className="space-y-1.5">
                    {tierData.coaching_cues.map((c, i) => (
                      <li key={i} className="text-xs text-white/70 flex items-start gap-2 leading-relaxed">
                        <span style={{ color: tierMeta.color }} className="mt-0.5 font-mono">{i + 1}.</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Mistakes */}
                <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Common Mistakes
                  </p>
                  <ul className="space-y-1.5">
                    {tierData.common_mistakes.map((m, i) => (
                      <li key={i} className="text-xs text-white/60 flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">✕</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Progression signal */}
                <div className="p-3 bg-emerald-500/5 border border-emerald-500/20">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" /> When to Level Up
                  </p>
                  <p className="text-xs text-white/70 leading-relaxed">{tierData.progression_signal}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, small = false }) => (
  <div className="bg-black/30 p-2 border border-white/[0.04]">
    <div className="flex items-center gap-1 text-[8px] font-mono uppercase tracking-wider text-white/40 mb-1">
      <Icon className="w-2.5 h-2.5" /> {label}
    </div>
    <p className={`${small ? "text-[10px]" : "text-xs"} font-mono text-white truncate`}>{value}</p>
  </div>
);

export default WeightRoomPage;
